import { useEffect, useState } from 'react'

import { Icon } from '../Icon/Icon'
import './DocumentCheckbox.css'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'

interface DocumentCheckboxProps {
  title?: string
  inputName: string
  description: string
  externalUrl?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleDecision: (event: any) => void
  value: boolean
  onClick?: () => void
  documentType?: string
  isRequired?: boolean
  showAsterisk?: boolean
  serviceType?: string
  goToDocumentLabel?: string
}

export const DocumentCheckbox = ({
  title,
  inputName,
  description,
  externalUrl,
  handleDecision,
  value,
  onClick,
  documentType,
  isRequired,
  showAsterisk,
  serviceType,
  goToDocumentLabel,
}: DocumentCheckboxProps) => {
  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const onChangeHandler = () => {
    const newInputValue = !inputValue

    handleDecision({
      checked: newInputValue,
      id: inputName,
      isRequired,
      documentType,
      serviceType,
    })

    setInputValue(newInputValue)
  }

  const titleId = title ? `${inputName}-title` : undefined
  const linkAriaLabel = title ? `${goToDocumentLabel}: ${title}` : goToDocumentLabel

  const goToDocument = () => {
    return onClick ? (
      <button
        className="doc-checkbox__go-to"
        aria-label={linkAriaLabel}
        onClick={onClick}
        data-testid={`${inputName}_link`}
        type="button"
      >
        <Icon type="external-link" size="18px" />
      </button>
    ) : (
      <a
        className="doc-checkbox__go-to"
        target="_blank"
        href={externalUrl}
        rel="noopener noreferrer"
        aria-label={linkAriaLabel}
        data-testid={`${inputName}_link`}
        tabIndex={0}
      >
        <Icon type="external-link" size="18px" />
      </a>
    )
  }

  return (
    <div className="doc-checkbox" key={inputName} data-has-title={!!title}>
      {title && (
        <div className="doc-checkbox__header">
          <h3 id={titleId} data-required={showAsterisk}>
            {title}
          </h3>
        </div>
      )}
      <div className="doc-checkbox__condition">
        <div className="doc-checkbox__content">
          <label
            className="doc-checkbox__main-label label-checkbox"
            htmlFor={inputName}
            data-testid={`${inputName}_label`}
            tabIndex={0}
            onKeyDown={(event) => onKeyDownHandler(event, () => onChangeHandler())}
          >
            <input
              id={inputName}
              type="checkbox"
              name={inputName}
              onChange={onChangeHandler}
              checked={inputValue}
              data-testid={`${inputName}_input`}
              aria-describedby={titleId}
              tabIndex={-1}
            />
            {description}
          </label>
          {(externalUrl || onClick) && goToDocument()}
        </div>
      </div>
    </div>
  )
}
